import leven from 'leven';
import type { ProductCatalogItem } from "@ozon-ai/ozon/contracts";

const MAX_RESULTS = 40;

/** 1,28 и 1.28 должны совпадать при поиске. */
export function normalizeSearchText(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/(\d),(\d)/g, '$1.$2');
}

function catalogFields(item: ProductCatalogItem): string[] {
  return [item.offerId, item.name, item.ozonSku].filter(Boolean) as string[];
}

function fieldParts(field: string): string[] {
  const normalized = normalizeSearchText(field);
  const parts = normalized.split(/[\s/\\|;,+()[\]{}]+/).filter(Boolean);
  return parts.length > 0 ? parts : [normalized];
}

function maxLevenDistance(queryLength: number): number {
  if (queryLength <= 2) return 1;
  if (queryLength <= 5) return 1;
  return 2;
}

/** Не сравниваем запрос с короткими кусками текста (иначе «esp32» ≈ «32» в SKU). */
function minChunkLength(queryLength: number, threshold: number): number {
  return Math.max(
    Math.ceil(queryLength * 0.8),
    queryLength - Math.min(threshold, 1),
  );
}

function isNumericIdField(field: string): boolean {
  return /^\d+$/.test(field.trim());
}

/** Fuzzy match query against part of text (prefix, substring, typo-tolerant). */
export function fuzzyMatchScore(
  query: string,
  text: string,
  options?: { numericId?: boolean },
): number | null {
  const normalizedQuery = normalizeSearchText(query);
  const normalizedText = normalizeSearchText(text);

  if (!normalizedQuery) {
    return 0;
  }

  if (normalizedText.includes(normalizedQuery)) {
    return 0;
  }

  if (options?.numericId) {
    return null;
  }

  const threshold = maxLevenDistance(normalizedQuery.length);
  let best: number | null = null;

  const minLen = minChunkLength(normalizedQuery.length, threshold);
  const maxLen = Math.min(normalizedText.length, normalizedQuery.length + threshold);

  for (let start = 0; start < normalizedText.length; start++) {
    for (let len = minLen; len <= maxLen; len++) {
      if (start + len > normalizedText.length) {
        continue;
      }

      const chunk = normalizedText.slice(start, start + len);
      const distance = leven(normalizedQuery, chunk);
      if (distance > threshold) {
        continue;
      }

      const denom = Math.max(normalizedQuery.length, len);
      if (distance / denom > 0.34) {
        continue;
      }

      const prefixBonus = start === 0 ? 0 : 1;
      const score = distance + prefixBonus;
      best = best === null ? score : Math.min(best, score);
    }
  }

  return best;
}

function scoreField(query: string, field: string): number | null {
  const numericId = isNumericIdField(field);
  const whole = fuzzyMatchScore(query, field, { numericId });
  if (whole !== null) {
    return whole;
  }

  let best: number | null = null;
  for (const part of fieldParts(field)) {
    const partScore = fuzzyMatchScore(query, part, {
      numericId: numericId || isNumericIdField(part),
    });
    if (partScore !== null) {
      best = best === null ? partScore : Math.min(best, partScore);
    }
  }

  return best;
}

export function scoreCatalogItem(item: ProductCatalogItem, query: string): number | null {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) {
    return 0;
  }

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
  let totalScore = 0;

  for (const token of tokens) {
    const tokenScores = catalogFields(item)
      .map((field) => scoreField(token, field))
      .filter((score): score is number => score !== null);

    if (tokenScores.length === 0) {
      return null;
    }

    totalScore += Math.min(...tokenScores);
  }

  return totalScore;
}

export function searchCatalog(
  catalog: ProductCatalogItem[],
  query: string,
  limit = MAX_RESULTS
): ProductCatalogItem[] {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) {
    return catalog.slice(0, limit);
  }

  return catalog
    .map((item) => ({ item, score: scoreCatalogItem(item, normalizedQuery) }))
    .filter((entry): entry is { item: ProductCatalogItem; score: number } => entry.score !== null)
    .sort((a, b) => a.score - b.score || a.item.name.localeCompare(b.item.name, 'ru'))
    .slice(0, limit)
    .map((entry) => entry.item);
}
